<template>
	<div class="wrapper">
		<b-row>
			<b-col cols="12">
				<b-card header-tag="header">
					<template v-slot:header>
						<i class="icon-settings"></i> Employee and Device Limits
					</template>

					<b-card-body>
						<b-form>
							<!-- Office selector -->
							<b-form-group label="Office" label-for="office" :label-cols="3" :horizontal="true">
								<multiselect
									id="office"
									v-model="selectedOffice"
									:options="officeList"
									:close-on-select="true"
									:searchable="true"
									label="name"
									placeholder="Pick a value"
									@input="loadSettingsForOffice()"
								/>
							</b-form-group>

							<!-- Total Employee Limit -->
							<b-form-group label="Total Employee Limit" :label-cols="3" :horizontal="true">
								<b-form-input
									type="number"
									v-model="employeeLimitValue"
									:readonly="!selectedOffice"
									min="0"
									step="1"
									:placeholder="selectedOffice ? 'eg. 25' : 'Enter total employee limit'"
								/>
							</b-form-group>

							<!-- Location Tracking User Limit -->
							<b-form-group label="Location Tracking User Limit" :label-cols="3" :horizontal="true">
								<b-form-input
									type="number"
									v-model="locationTrackingLimitValue"
									:readonly="!selectedOffice"
									min="0"
									step="1"
									:placeholder="selectedOffice ? 'eg. 0' : 'Enter location tracking user limit'"
								/>
							</b-form-group>

							<!-- Total Device Limit -->
							<b-form-group label="Total Device Limit" :label-cols="3" :horizontal="true">
								<b-form-input
									type="number"
									v-model="deviceLimitValue"
									:readonly="!selectedOffice"
									min="0"
									step="1"
									:placeholder="selectedOffice ? 'eg. 2' : 'Enter total device limit'"
								/>
							</b-form-group>

							<!-- Save -->
							<div class="form-actions">
								<b-button type="button" variant="success" :disabled="isAllFieldsFilled" @click="save">Save</b-button>
							</div>
						</b-form>
					</b-card-body>
				</b-card>
			</b-col>
		</b-row>
	</div>
</template>

<script>
import toastService from "@/services/toastService";
import officeService from "@/services/officeService";
import generalSettingsService from "@/services/generalSettingsService";
import generalSettingsGroupIds from "@/services/generalSettingsGroupIds";
import Multiselect from "vue-multiselect";

const KEY_EMPLOYEE_LIMIT = "TOTAL_EMPLOYEE_LIMIT";
const KEY_LOCATION_TRACKING_USER_LIMIT = "LOCATION_TRACKING_USER_LIMIT";
const KEY_DEVICE_LIMIT = "TOTAL_DEVICE_LIMIT";

export default {
	name: "EmployeeDeviceLimitsSettings",
	components: { Multiselect },
	data() {
		return {
			officeList: [],
			selectedOffice: null,

			employeeLimitRow: null,
			locationTrackingLimitRow: null,
			deviceLimitRow: null,

			employeeLimitValue: null,
			locationTrackingLimitValue: null,
			deviceLimitValue: null
		};
	},

	computed: {
			isAllFieldsFilled() {
				return (this.employeeLimitValue == null || this.employeeLimitValue == "") &&
					(this.locationTrackingLimitValue == null || this.locationTrackingLimitValue == "") &&
					(this.deviceLimitValue == null || this.deviceLimitValue == "");
			}
	},

	methods: {
		loadSettingsForOffice() {
			this.employeeLimitRow = null;
			this.locationTrackingLimitRow = null;
			this.deviceLimitRow = null;

			if (!this.selectedOffice) {
				this.employeeLimitValue = null;
				this.locationTrackingLimitValue = null;
				this.deviceLimitValue = null;
				return;
			}

			this.employeeLimitValue = null;
			this.locationTrackingLimitValue = null;
			this.deviceLimitValue = null;

			generalSettingsService.getDeviceAndEmployeeLimit(
				this.selectedOffice.id,
				(settings) => {
					const empRow = settings.find(s => s.keyCode === KEY_EMPLOYEE_LIMIT);
					const ltRow  = settings.find(s => s.keyCode === KEY_LOCATION_TRACKING_USER_LIMIT);
					const devRow = settings.find(s => s.keyCode === KEY_DEVICE_LIMIT);

					if (empRow) {
						this.employeeLimitRow  = empRow;
						this.employeeLimitValue = empRow.setValue != null && empRow.setValue !== "" ? empRow.setValue : null;
					}
					if (ltRow) {
						this.locationTrackingLimitRow  = ltRow;
						this.locationTrackingLimitValue = ltRow.setValue != null && ltRow.setValue !== "" ? ltRow.setValue : null;
					}
					if (devRow) {
						this.deviceLimitRow  = devRow;
						this.deviceLimitValue = devRow.setValue != null && devRow.setValue !== "" ? devRow.setValue : null;
					}
				},
				(msg) => toastService.showErrorToast(msg || "Failed to load settings")
			);
		},

		save() {
			if (!this.selectedOffice) {
				return toastService.showErrorToast("Please select an office");
			}

			if (this.employeeLimitValue != null && this.employeeLimitValue < 0) {
				this.employeeLimitValue = this.employeeLimitRow && this.employeeLimitRow.setValue != null ? this.employeeLimitRow.setValue : null;
				return toastService.showErrorToast("Total Employee Limit cannot be negative");
			}

			if (this.locationTrackingLimitValue != null && this.locationTrackingLimitValue < 0) {
				this.locationTrackingLimitValue = this.locationTrackingLimitRow && this.locationTrackingLimitRow.setValue != null ? this.locationTrackingLimitRow.setValue : null;
				return toastService.showErrorToast("Location Tracking User Limit cannot be negative");
			}

			if (this.deviceLimitValue != null && this.deviceLimitValue < 0) {
				this.deviceLimitValue = this.deviceLimitRow && this.deviceLimitRow.setValue != null ? this.deviceLimitRow.setValue : null;
				return toastService.showErrorToast("Total Device Limit cannot be negative");
			}

			const settings = [
				{
					keyCode: KEY_EMPLOYEE_LIMIT,
					setValue: this.employeeLimitValue != null && this.employeeLimitValue !== "" ? String(this.employeeLimitValue) : null,
					groupId: generalSettingsGroupIds.EMPLOYEE_DEVICE_LIMITS_ID,
					keyId: 0,
					officeGroupKeyId:  null
				},
				{
					keyCode: KEY_LOCATION_TRACKING_USER_LIMIT,
					setValue: this.locationTrackingLimitValue != null && this.locationTrackingLimitValue !== "" ? String(this.locationTrackingLimitValue) : null,
					groupId: generalSettingsGroupIds.EMPLOYEE_DEVICE_LIMITS_ID,
					keyId: 0,
					officeGroupKeyId:  null
				},
				{
					keyCode: KEY_DEVICE_LIMIT,
					setValue: this.deviceLimitValue != null && this.deviceLimitValue !== "" ? String(this.deviceLimitValue) : null,
					groupId: generalSettingsGroupIds.EMPLOYEE_DEVICE_LIMITS_ID,
					keyId: 0,
					officeGroupKeyId: null
				}
			];

			const payload = {
				officeId: this.selectedOffice.id,
				groupId: generalSettingsGroupIds.EMPLOYEE_DEVICE_LIMITS_ID,
				settings: settings
			};

			generalSettingsService.deviceAndEmployeeLimit(
				payload,
				() => {
					toastService.showSuccessToast("Updated successfully");
					this.loadSettingsForOffice();
				},
				() => toastService.showErrorToast("Update failed")
			);
		}
	},
	mounted() {
		officeService.getOfficeList(
			r => { this.officeList = r; },
			() => toastService.showErrorToast("Failed to load office list")
		);
	}
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
/* Grayish placeholder for inputs */
input::placeholder {
	color: #aab2bd !important;
	opacity: 1; /* Firefox */
}

input:-ms-input-placeholder {
	color: #aab2bd !important;
}

input::-ms-input-placeholder {
	color: #aab2bd !important;
}
</style>
